import {
  type HTMLAttributes,
  type KeyboardEvent,
  type PointerEvent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo
} from 'react'
import { cn } from '../utils'
import { DarkModeContext } from '../contexts/DarkModeContext'
import { ArrowForwardIcon20 } from '../icons'
import { useTranslation } from 'react-i18next'

const THUMB_HEIGHT = 35
const THUMB_WIDTH = 55
const PADDING = 4
const KEYBOARD_STEP = 0.05

function clamp(value: number, min = 0, max = 1): number {
  return Math.min(max, Math.max(min, value))
}

function lerpChannel(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t)
}

export interface SlideToUnlockProps extends HTMLAttributes<HTMLDivElement> {
  confirmed: boolean
  onConfirmedChange?: (confirmed: boolean) => void
  threshold?: number
  releaseThreshold?: number
  disabled?: boolean
  'data-testid': string
  variant?: 'brand-primary' | 'secondary-solid'
}

export default function SlideToUnlock({
  confirmed,
  onConfirmedChange,
  threshold = 0.88,
  releaseThreshold = 0.12,
  disabled = false,
  className,
  'data-testid': testId,
  variant = 'brand-primary',
  ...props
}: PropsWithChildren<SlideToUnlockProps>) {
  const { darkMode } = useContext(DarkModeContext)
  const { t } = useTranslation('ui-components')
  const label = t('slide_to_unlock.abarbeiten')
  const confirmedLabel = t('slide_to_unlock.zuruecksetzen')

  const trackRef = useRef<HTMLDivElement>(null)
  const thumbRef = useRef<HTMLButtonElement>(null)
  const dragOffsetRef = useRef(THUMB_HEIGHT / 2)

  const [pos, setPos] = useState(confirmed ? 1 : 0)
  const posRef = useRef(pos)
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    if (!dragging) setPos(confirmed ? 1 : 0)
  }, [confirmed, dragging])

  const hexLerp = useCallback((hex1: string, hex2: string, t: number) => {
    const parse = (hex: string, offset: number) =>
      Number.parseInt(hex.slice(offset, offset + 2), 16)
    const r = lerpChannel(parse(hex1, 1), parse(hex2, 1), t)
    const g = lerpChannel(parse(hex1, 3), parse(hex2, 3), t)
    const b = lerpChannel(parse(hex1, 5), parse(hex2, 5), t)
    return `rgb(${r},${g},${b})`
  }, [])

  const COLORS = useMemo(() => {
    if (variant === 'brand-primary') {
      return {
        light: { from: '#EC0016', to: '#000000' },
        dark: { from: '#9b000e', to: '#646973' }
      }
    }

    return {
      light: { from: '#AFB4BB', to: '#3C414B' },
      dark: { from: '#878C96', to: '#3C414B' }
    }
  }, [variant])

  const posFromClientX = useCallback((clientX: number) => {
    const track = trackRef.current
    if (!track) return
    const rect = track.getBoundingClientRect()
    const usable = Math.max(rect.width - THUMB_HEIGHT - PADDING * 2, 1)
    setPos(
      clamp((clientX - rect.left - dragOffsetRef.current - PADDING) / usable)
    )
  }, [])

  const commitAtPosition = useCallback(
    (p: number) => {
      if (!confirmed && p >= threshold) {
        posRef.current = 1
        setPos(1)
        onConfirmedChange?.(true)
      } else if (confirmed && p <= releaseThreshold) {
        posRef.current = 0
        setPos(0)
        onConfirmedChange?.(false)
      } else {
        const snap = confirmed ? 1 : 0
        posRef.current = snap
        setPos(snap)
      }
    },
    [confirmed, onConfirmedChange, threshold, releaseThreshold]
  )

  const handlePointerDown = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      if (disabled) return
      e.preventDefault()
      dragOffsetRef.current = clamp(
        e.clientX - e.currentTarget.getBoundingClientRect().left,
        0,
        THUMB_HEIGHT
      )
      e.currentTarget.setPointerCapture(e.pointerId)
      setDragging(true)
      posFromClientX(e.clientX)
      thumbRef.current?.focus()
    },
    [posFromClientX, disabled]
  )

  const handlePointerMove = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      if (!dragging || disabled) return
      posFromClientX(e.clientX)
    },
    [posFromClientX, dragging, disabled]
  )

  const handlePointerUp = useCallback(
    (e: PointerEvent<HTMLButtonElement>) => {
      if (!dragging) return
      e.currentTarget.releasePointerCapture?.(e.pointerId)
      e.currentTarget.blur()
      dragOffsetRef.current = THUMB_HEIGHT / 2
      setDragging(false)
      commitAtPosition(pos)
    },
    [commitAtPosition, dragging, pos]
  )

  const handlePointerCancel = useCallback(() => {
    if (!dragging) return
    dragOffsetRef.current = THUMB_HEIGHT / 2
    setDragging(false)
    setPos(confirmed ? 1 : 0)
  }, [dragging, confirmed])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return
      let next: number

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowUp':
          e.preventDefault()
          next = clamp(posRef.current + KEYBOARD_STEP)
          break
        case 'ArrowLeft':
        case 'ArrowDown':
          e.preventDefault()
          next = clamp(posRef.current - KEYBOARD_STEP)
          break
        case 'Home':
          e.preventDefault()
          next = 0
          break
        case 'End':
          e.preventDefault()
          next = 1
          break
        case 'Enter':
        case ' ':
          e.preventDefault()
          commitAtPosition(posRef.current)
          return
        default:
          return
      }
      posRef.current = next
      setPos(next)
    },
    [commitAtPosition, disabled]
  )

  const handleBlur = useCallback(() => {
    if (!dragging) commitAtPosition(posRef.current)
  }, [commitAtPosition, dragging])

  const pct = Math.round(pos * 100)
  const text = confirmed ? confirmedLabel : label
  const thumbLeft = `calc(${PADDING}px + ${pos} * (100% - ${THUMB_WIDTH + PADDING * 2}px))`

  const palette = darkMode ? COLORS.dark : COLORS.light
  const trackBg = hexLerp(palette.from, palette.to, pos)
  const labelColor = useMemo(() => {
    if (variant == 'brand-primary') {
      return '#ffffff'
    } else {
      return hexLerp('#131821', '#ffffff', pos)
    }
  }, [variant, pos, hexLerp])
  return (
    <div
      className={cn('w-full', className)}
      {...props}
      data-testid={`${testId}-slide-to-unlock-wrapper`}
    >
      <div
        ref={trackRef}
        className={cn(
          'relative select-none overflow-hidden rounded-[8px] z-40',
          disabled && 'opacity-50',
          variant == 'secondary-solid' && 'dark:border'
        )}
        style={{
          backgroundColor: trackBg,
          transition: dragging
            ? 'none'
            : 'background-color 0.35s cubic-bezier(.4,0,.2,1)'
        }}
        data-testid={`${testId}-slide-to-unlock-track-outer`}
      >
        <div
          className="flex flex-col items-center justify-center text-sm font-semibold tracking-tight"
          style={{
            paddingLeft: THUMB_HEIGHT + PADDING * 2 + 8,
            paddingRight: THUMB_HEIGHT + PADDING * 2 + 8,
            minHeight: THUMB_HEIGHT + PADDING * 2
          }}
          aria-hidden="true"
        >
          <span className="text-center opacity-0 pointer-events-none select-none">
            {label}
          </span>
          <span className="text-center opacity-0 pointer-events-none select-none">
            {confirmedLabel}
          </span>
        </div>
        <div
          className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-semibold tracking-tight"
          style={{
            paddingLeft: THUMB_HEIGHT + PADDING * 2 + 8,
            paddingRight: THUMB_HEIGHT + PADDING * 2 + 8,
            minHeight: THUMB_HEIGHT + PADDING * 2,
            color: labelColor,
            transition: dragging
              ? 'none'
              : 'color 0.35s cubic-bezier(.4,0,.2,1)'
          }}
          data-testid={`${testId}-slide-to-unlock-track-inner`}
        >
          <span
            className="text-center"
            data-testid={`${testId}-slide-to-unlock-label`}
          >
            {text}
          </span>
        </div>

        <button
          ref={thumbRef}
          type="button"
          role="slider"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          aria-valuetext={`${pct}%`}
          aria-disabled={disabled}
          disabled={disabled}
          className={cn(
            'absolute top-1/2 -translate-y-1/2 touch-none',
            'flex items-center justify-center rounded-[4px]',
            'bg-white text-cool-gray-800 z-40',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2',
            dragging
              ? 'scale-105 shadow-lg cursor-grabbing'
              : 'scale-100 shadow-md cursor-grab',
            disabled && 'cursor-default'
          )}
          style={{
            width: THUMB_WIDTH,
            height: THUMB_HEIGHT,
            left: thumbLeft,
            transition: dragging
              ? 'transform 0.15s cubic-bezier(.4,0,.2,1), box-shadow 0.15s cubic-bezier(.4,0,.2,1)'
              : 'left 0.3s cubic-bezier(.4,0,.2,1), transform 0.2s cubic-bezier(.4,0,.2,1), box-shadow 0.2s cubic-bezier(.4,0,.2,1)'
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          data-testid={`${testId}-slide-to-unlock-thumb`}
        >
          <ArrowForwardIcon20
            className={cn(
              'transition-transform duration-300',
              confirmed && 'rotate-180'
            )}
            data-testid={`${testId}-slide-to-unlock-icon`}
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  )
}
